import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import { useCallback } from "react";
import { useLearnMapModeSelector } from "#use-learn-map-mode";

/**
 * Leaves Learn's map for the cards from a press: the map's top-left control, a press of the
 * focused Learn tab, and the home's Start. The cards start returning at once and the route follows
 * their slide (`switchMode`).
 *
 * From the home, the summary's exit decides where the cards open (`LearnMapHomeExit`: the deck on
 * the next card, when there is one). Otherwise, or with no exit registered yet, the map goes back to
 * the cards it lies over, and leaving the home that way ends it too.
 */
export function useLeaveLearnMap(): () => void {
  const { goBack } = useScopedBack();
  const goBackRef = useLatest(goBack);
  const [setHasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasLearnFlowStartedThisSession,
  );
  const isMapHome = useLearnMapModeSelector((mode) => mode.isMapHome) ?? false;
  const mapHomeExitRef = useLearnMapModeSelector((mode) => mode.mapHomeExitRef);
  const switchMode = useLearnMapModeSelector((mode) => mode.switchMode);
  return useCallback(() => {
    const back = () => {
      // A map opened at launch or by a deep link has no cards route behind it in the stack.
      goBackRef.current({ fallbackHref: "/(nav)/(app)/learn/summary" });
      setHasLearnFlowStartedThisSession(true);
    };
    // Taken with the press: the slide runs first, and the exit opens the cards it slid in.
    const exit = isMapHome ? (mapHomeExitRef?.current ?? null) : null;
    const open = exit === null ? null : exit();
    const leave = () => (open === null ? back() : open(back));
    if (switchMode === undefined) {
      leave();
    } else {
      switchMode(false, leave);
    }
  }, [goBackRef, isMapHome, mapHomeExitRef, setHasLearnFlowStartedThisSession, switchMode]);
}
